import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './ProductList.module.scss';
import CircleArrowButton from '@/components/CircleArrowButton';

export interface ProductListItem {
  img: string;
  title: string;
  desc: string;
  summary: string;
}

interface ProductListProps {
  list: ProductListItem[];
}

// 每屏可见数量；超过该数量时展示左右按钮并支持滚动
const VISIBLE_COUNT = 3;
// 单个卡片宽度 + 间距，用于计算滚动位移（与 scss 中保持一致）
const ITEM_STEP_DEFAULT = 347 + 16;
const ITEM_STEP_COMPACT = 306 + 12;
const COMPACT_MAX_HEIGHT = 768;
const ITEM_MAX_HEIGHT_DEFAULT = 148;
const ITEM_MIN_HEIGHT_COMPACT = 132;

const getItemStep = (height: number) =>
  height <= COMPACT_MAX_HEIGHT ? ITEM_STEP_COMPACT : ITEM_STEP_DEFAULT;
const getItemMaxHeight = (height: number) =>
  height <= COMPACT_MAX_HEIGHT ? ITEM_MIN_HEIGHT_COMPACT : ITEM_MAX_HEIGHT_DEFAULT;
// 首尾各克隆 VISIBLE_COUNT 个卡片，用于无缝循环
const CLONES = VISIBLE_COUNT;

export default function ProductList({ list }: ProductListProps) {
  const realCount = list.length;
  const scrollable = realCount > VISIBLE_COUNT;

  const [itemStep, setItemStep] = useState(() =>
    typeof window !== 'undefined' ? getItemStep(window.innerHeight) : ITEM_STEP_DEFAULT,
  );
  const [itemMaxHeight, setItemMaxHeight] = useState(() =>
    typeof window !== 'undefined' ? getItemMaxHeight(window.innerHeight) : ITEM_MAX_HEIGHT_DEFAULT,
  );

  useEffect(() => {
    const updateItemStep = () => setItemStep(getItemStep(window.innerHeight));
    const updateItemMaxHeight = () => setItemMaxHeight(getItemMaxHeight(window.innerHeight));
    updateItemStep();
    updateItemMaxHeight();
    window.addEventListener('resize', updateItemStep);
    window.addEventListener('resize', updateItemMaxHeight);
    return () => {
      window.removeEventListener('resize', updateItemStep);
      window.removeEventListener('resize', updateItemMaxHeight);
    };
  }, []);

  const trackRef = useRef<HTMLDivElement>(null);
  // pos 为「真实列表」起始位置，初始指向第一个真实卡片
  const [pos, setPos] = useState(CLONES);

  const goPrev = useCallback(() => {
    setPos((prev) => (prev > 0 ? prev - 1 : prev));
  }, []);

  const goNext = useCallback(() => {
    setPos((prev) => (prev < CLONES + realCount ? prev + 1 : prev));
  }, [realCount]);

  // 滑到克隆区后，过渡结束时无动画地跳回对应的真实位置，实现无缝循环
  const handleTransitionEnd = useCallback(
    (e: React.TransitionEvent) => {
      if (e.target !== trackRef.current || e.propertyName !== 'transform') return;
      const track = trackRef.current;
      if (!track) return;

      const snapTo = (next: number) => {
        track.style.transition = 'none';
        setPos(next);
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            track.style.transition = '';
          });
        });
      };

      if (pos === 0) {
        snapTo(realCount);
      } else if (pos === CLONES + realCount) {
        snapTo(CLONES);
      }
    },
    [pos, realCount],
  );

  const handleItemHover = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const item = e.currentTarget;
    const content = item.querySelector<HTMLDivElement>(`.${styles['item-content']}`);
    const desc = item.querySelector<HTMLDivElement>(`.${styles['item-desc']}`);
    if (desc) {
      desc.textContent = desc.getAttribute('data-desc') || '';
    }
    if (content) {
      // 切换文案后，scrollHeight 即为内容（含 padding）的真实高度，据此撑开
      content.style.height = `${Math.max(content.scrollHeight, itemMaxHeight)}px`;
    }
  }, []);

  const handleItemLeave = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const item = e.currentTarget;
    const content = item.querySelector<HTMLDivElement>(`.${styles['item-content']}`);
    const desc = item.querySelector<HTMLDivElement>(`.${styles['item-desc']}`);
    if (content) {
      content.style.height = '';
    }
    if (desc) {
      desc.textContent = desc.getAttribute('data-summary') || '';
    }
  }, []);

  const renderItem = (item: ProductListItem, key: string) => (
    <div
      key={key}
      className={styles['item']}
      onMouseEnter={handleItemHover}
      onMouseLeave={handleItemLeave}
    >
      <img src={item.img} alt={item.title} className={styles['item-img']} />
      <div className={styles['item-content']}>
        <div className={styles['item-title']}>{item.title}</div>
        <div className={styles['item-text']}>
          <div className={styles['item-desc']} data-desc={item.desc} data-summary={item.summary}>
            {item.summary}
          </div>
        </div>
      </div>
    </div>
  );

  if (!scrollable) {
    return <div className={styles['list']}>{list.map((item) => renderItem(item, item.title))}</div>;
  }

  // [尾部克隆, 真实列表, 头部克隆]
  const extended = [...list.slice(realCount - CLONES), ...list, ...list.slice(0, CLONES)];

  return (
    <div className={styles['carousel']}>
      <CircleArrowButton direction="left" onClick={goPrev} className={styles['button-left']} />
      <div className={styles['viewport']}>
        <div
          ref={trackRef}
          className={styles['track']}
          style={{ transform: `translateX(-${pos * itemStep}px)` }}
          onTransitionEnd={handleTransitionEnd}
        >
          {extended.map((item, idx) => renderItem(item, `${item.title}-${idx}`))}
        </div>
      </div>
      <CircleArrowButton direction="right" onClick={goNext} className={styles['button-right']} />
    </div>
  );
}
